import { SPECTROGRAM_BINS, SPECTROGRAM_FRAMES } from '../data/microphone';

/**
 * Audio sample binary format + spectrogram helpers.
 *
 * Phase 1 ships TM-style audio: a long recording (e.g. 20s for background
 * noise, 2s for a normal class) is captured in one go, the kid listens to
 * it, then clicks "Extract Sample" to slice the recording into 1-second
 * chunks. Each chunk is stored as a `Sample.blob` containing a small WAV
 * file so the kid can play it back, delete it, and trust what they have
 * captured. Spectrograms are computed at training and predict time.
 *
 * Why WAV: the browser's `<audio>` element plays it without any decoder
 * dance, every browser supports the encoding we use (mono 16-bit PCM),
 * and it survives a `.bryta` save/open round-trip cleanly.
 */

export const AUDIO_SAMPLE_MIME = 'audio/wav';
/** Sample rate we capture and store at. Web Audio's default is 44.1k or
 *  48k; we resample on encode to keep blobs small and uniform. */
export const TARGET_SAMPLE_RATE = 16_000;
/** A single training/eval sample is exactly 1 second of audio. */
export const SAMPLE_DURATION_SECONDS = 1;
/** The default duration kids record at for a regular class — TM ships
 *  with 2 seconds; the kid sees a 2-second recording, then "Extract
 *  Sample" turns it into 2 × 1-second clips. */
export const DEFAULT_RECORD_SECONDS = 2;
/** The default duration for the Background noise class — same as TM. */
export const BACKGROUND_NOISE_RECORD_SECONDS = 20;

/** Encode a Float32 mono PCM buffer (range -1..1) at `sampleRate` into a
 *  WAV blob. Output is 16-bit PCM, little-endian, single channel. */
export function encodeWavBlob(pcm: Float32Array, sampleRate: number): Blob {
  const dataBytes = pcm.length * 2;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);

  // RIFF header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeString(view, 8, 'WAVE');
  // fmt subchunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // subchunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate (mono × 2 bytes/sample)
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  // data subchunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataBytes, true);
  // PCM samples — clamp -1..1 then quantize to int16.
  for (let i = 0; i < pcm.length; i++) {
    const v = pcm[i] ?? 0;
    const clamped = Math.max(-1, Math.min(1, v));
    view.setInt16(44 + i * 2, Math.round(clamped * 0x7fff), true);
  }
  return new Blob([buffer], { type: AUDIO_SAMPLE_MIME });
}

/** Decode a WAV blob back into mono Float32 PCM and its sample rate.
 *  Throws if the bytes are not a 16-bit PCM mono WAV. */
export async function decodeWavBlob(
  blob: Blob,
): Promise<{ pcm: Float32Array; sampleRate: number }> {
  const buf = await blob.arrayBuffer();
  if (buf.byteLength < 44) {
    throw new Error('Audio sample is too small to be a WAV.');
  }
  const view = new DataView(buf);
  if (
    readString(view, 0, 4) !== 'RIFF' ||
    readString(view, 8, 4) !== 'WAVE' ||
    readString(view, 12, 4) !== 'fmt '
  ) {
    throw new Error('Audio sample is not in WAV format.');
  }
  const format = view.getUint16(20, true);
  const channels = view.getUint16(22, true);
  const sampleRate = view.getUint32(24, true);
  const bitsPerSample = view.getUint16(34, true);
  if (format !== 1 || bitsPerSample !== 16 || channels !== 1) {
    throw new Error('Only 16-bit mono PCM WAV is supported.');
  }
  // Walk the chunks to find the data subchunk — fmt isn't always exactly
  // 16 bytes wide.
  let offset = 12;
  while (offset < view.byteLength) {
    const id = readString(view, offset, 4);
    const size = view.getUint32(offset + 4, true);
    if (id === 'data') {
      const samples = size / 2;
      const pcm = new Float32Array(samples);
      for (let i = 0; i < samples; i++) {
        pcm[i] = (view.getInt16(offset + 8 + i * 2, true) ?? 0) / 0x8000;
      }
      return { pcm, sampleRate };
    }
    offset += 8 + size;
  }
  throw new Error('Audio sample is missing its data chunk.');
}

/** Resample a mono PCM buffer to a target sample rate using linear
 *  interpolation. Good enough for the small classifier — we're not
 *  shipping high-fidelity audio, we're shipping a kid-trainable model. */
export function resamplePcm(pcm: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return pcm;
  const ratio = toRate / fromRate;
  const out = new Float32Array(Math.floor(pcm.length * ratio));
  for (let i = 0; i < out.length; i++) {
    const src = i / ratio;
    const lo = Math.floor(src);
    const hi = Math.min(pcm.length - 1, lo + 1);
    const frac = src - lo;
    out[i] = (pcm[lo] ?? 0) * (1 - frac) + (pcm[hi] ?? 0) * frac;
  }
  return out;
}

/**
 * Decode any browser-supported audio file (WAV, MP3, OGG, WebM, etc.)
 * into mono PCM at its native sample rate. WAV files take the cheap
 * path through `decodeWavBlob` so file upload works in unit tests
 * without an AudioContext; everything else routes through Web Audio's
 * `decodeAudioData`. Channels are averaged into a single mono channel
 * — the audio classifier only ever trains on mono.
 */
export async function decodeAudioFile(
  file: Blob,
): Promise<{ pcm: Float32Array; sampleRate: number }> {
  // Cheap path: the file is one of our own captured samples (WAV).
  if (file.type === AUDIO_SAMPLE_MIME || file.type === 'audio/x-wav') {
    return decodeWavBlob(file);
  }
  if (
    typeof window === 'undefined' ||
    (!window.AudioContext && !('webkitAudioContext' in window))
  ) {
    throw new Error('This browser cannot decode that audio format.');
  }
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const context = new Ctor();
  try {
    const buf = await file.arrayBuffer();
    // decodeAudioData mutates its input buffer on some browsers, so copy
    // first to avoid surprising the caller.
    const copy = new ArrayBuffer(buf.byteLength);
    new Uint8Array(copy).set(new Uint8Array(buf));
    const audioBuffer = await context.decodeAudioData(copy);
    const channels = audioBuffer.numberOfChannels;
    const length = audioBuffer.length;
    const mono = new Float32Array(length);
    if (channels === 1) {
      mono.set(audioBuffer.getChannelData(0));
    } else {
      // Average non-mono inputs into a single channel.
      for (let c = 0; c < channels; c++) {
        const data = audioBuffer.getChannelData(c);
        for (let i = 0; i < length; i++) mono[i] = (mono[i] ?? 0) + (data[i] ?? 0);
      }
      for (let i = 0; i < length; i++) mono[i] = (mono[i] ?? 0) / channels;
    }
    return { pcm: mono, sampleRate: audioBuffer.sampleRate };
  } finally {
    if (context.state !== 'closed') {
      void context.close().catch(() => {
        // ignore
      });
    }
  }
}

/**
 * Decode a file the kid uploaded, resample to the canonical rate, and
 * slice into 1-second WAV blobs ready for storage as training clips.
 * Drops files shorter than one second (would just be one mostly-empty
 * clip) — surfaces a clear error so the kid knows why.
 */
export async function audioFileToSampleBlobs(file: Blob): Promise<Blob[]> {
  const { pcm, sampleRate } = await decodeAudioFile(file);
  const downsampled = resamplePcm(pcm, sampleRate, TARGET_SAMPLE_RATE);
  const slices = sliceIntoSeconds(downsampled, TARGET_SAMPLE_RATE, SAMPLE_DURATION_SECONDS);
  if (slices.length === 0) {
    throw new Error('That audio file is shorter than 1 second. Try a longer recording.');
  }
  return slices.map((slice) => encodeWavBlob(slice, TARGET_SAMPLE_RATE));
}

/** Slice a PCM buffer into N equal pieces, dropping any trailing tail
 *  shorter than one piece. Used by the MicPanel's "Extract Sample" flow
 *  to turn a longer recording into 1-second training clips. */
export function sliceIntoSeconds(
  pcm: Float32Array,
  sampleRate: number,
  secondsPerSlice: number = SAMPLE_DURATION_SECONDS,
): Float32Array[] {
  const samplesPerSlice = Math.floor(secondsPerSlice * sampleRate);
  if (samplesPerSlice <= 0) return [];
  const slices: Float32Array[] = [];
  for (let start = 0; start + samplesPerSlice <= pcm.length; start += samplesPerSlice) {
    slices.push(pcm.slice(start, start + samplesPerSlice));
  }
  return slices;
}

/**
 * Compute a log-magnitude spectrogram of a mono PCM buffer using a sliding
 * Hann-windowed DFT. Output is `frames × bins`, in roughly-dBFS range —
 * matching what the AnalyserNode produces at capture time, so the CNN
 * trains on inputs identical in distribution to what live prediction
 * sees.
 *
 * Pure JS, no TF.js dependency. The CNN input is small (43×232≈10K floats)
 * so a hand-rolled DFT is fine for ≤43 frames per call; we don't need a
 * full FFT library.
 */
export function pcmToSpectrogram(
  pcm: Float32Array,
  // Kept in the signature so callers always thread their sample
  // rate through. The current DFT derives bin spacing from the
  // buffer length, but a future mel-bank version would need this
  // rate to scale the filterbank. The leading underscore tells the
  // unused-vars rule it's intentional.
  _sampleRate: number,
  frames: number = SPECTROGRAM_FRAMES,
  bins: number = SPECTROGRAM_BINS,
): Float32Array {
  const out = new Float32Array(frames * bins);
  // The window length matches what an AnalyserNode with fftSize=1024 sees:
  // 1024 samples per frame at the source rate, but we may have resampled,
  // so derive it from the buffer + frame count.
  const windowSamples = Math.max(2, Math.floor(pcm.length / frames));
  const hann = hannWindow(windowSamples);
  // We need at least `bins` distinct frequencies — for short windows we
  // pad to the next power of two so the DFT bin spacing is finer than
  // `bins`.
  let dftSize = 1;
  while (dftSize < windowSamples || dftSize / 2 < bins) dftSize *= 2;
  const re = new Float32Array(dftSize);
  const im = new Float32Array(dftSize);
  // Frequency-domain bin spacing: sampleRate / dftSize. We crop to `bins`
  // low-frequency bins (mirrors how SC + the analyser adapter crops).
  for (let f = 0; f < frames; f++) {
    const start = Math.floor((f * (pcm.length - windowSamples)) / Math.max(1, frames - 1));
    re.fill(0);
    im.fill(0);
    for (let i = 0; i < windowSamples; i++) {
      re[i] = (pcm[start + i] ?? 0) * (hann[i] ?? 0);
    }
    fftInPlace(re, im);
    for (let b = 0; b < bins; b++) {
      const r = re[b] ?? 0;
      const i = im[b] ?? 0;
      const mag = Math.sqrt(r * r + i * i) + 1e-9;
      // Convert to dBFS-ish so the value range matches AnalyserNode's
      // getFloatFrequencyData (-100..0).
      out[f * bins + b] = 20 * Math.log10(mag);
    }
  }
  return out;
}

/** Normalize spectrogram values into a small, zero-centered range so
 *  gradient descent stays sane. dBFS in [-100, 0] → [-1, 1]. Used by both
 *  the trainer (offline) and the predict loop (online). */
export function normalizeSpectrogram(data: Float32Array): Float32Array {
  const out = new Float32Array(data.length);
  for (let i = 0; i < data.length; i++) {
    const v = data[i] ?? -100;
    out[i] = Math.max(-2, Math.min(2, v / 50 + 1));
  }
  return out;
}

function writeString(view: DataView, offset: number, value: string): void {
  for (let i = 0; i < value.length; i++) view.setUint8(offset + i, value.charCodeAt(i));
}

function readString(view: DataView, offset: number, length: number): string {
  let s = '';
  for (let i = 0; i < length; i++) s += String.fromCharCode(view.getUint8(offset + i));
  return s;
}

function hannWindow(n: number): Float32Array {
  const w = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    w[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (n - 1)));
  }
  return w;
}

/** In-place radix-2 Cooley–Tukey FFT. `re` and `im` must be the same
 *  length, a power of two. */
function fftInPlace(re: Float32Array, im: Float32Array): void {
  const n = re.length;
  // Bit-reversal permutation.
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    while (j & bit) {
      j ^= bit;
      bit >>= 1;
    }
    j |= bit;
    if (i < j) {
      [re[i], re[j]] = [re[j]!, re[i]!];
      [im[i], im[j]] = [im[j]!, im[i]!];
    }
  }
  for (let size = 2; size <= n; size <<= 1) {
    const half = size >> 1;
    const angleStep = (-2 * Math.PI) / size;
    for (let start = 0; start < n; start += size) {
      for (let k = 0; k < half; k++) {
        const angle = angleStep * k;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const aIdx = start + k;
        const bIdx = aIdx + half;
        const tre = (re[bIdx] ?? 0) * cos - (im[bIdx] ?? 0) * sin;
        const tim = (re[bIdx] ?? 0) * sin + (im[bIdx] ?? 0) * cos;
        re[bIdx] = (re[aIdx] ?? 0) - tre;
        im[bIdx] = (im[aIdx] ?? 0) - tim;
        re[aIdx] = (re[aIdx] ?? 0) + tre;
        im[aIdx] = (im[aIdx] ?? 0) + tim;
      }
    }
  }
}
